import { useState, useEffect } from "react";
import { Mail, Phone, Pencil, Star, Trash2, User } from "lucide-react";
import {
  listFarmContacts,
  addFarmContact,
  updateFarmContact,
  deleteFarmContact,
  updatePerson,
  type FarmContact,
} from "../../../api/admin";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import { Modal } from "../../primitives/Modal";
import { Select } from "../../primitives/Select";

type ClientContactsSectionProps = {
  farmId: string;
  onContactAdded?: () => void;
};

const CONTACT_ROLES = [
  { value: "owner", label: "Propriétaire" },
  { value: "manager", label: "Gestionnaire" },
  { value: "employee", label: "Employé" },
  { value: "agronomist", label: "Agronome" },
  { value: "accounting", label: "Comptabilité" },
  { value: "other", label: "Autre" },
];

export function ClientContactsSection({ farmId, onContactAdded }: ClientContactsSectionProps) {
  const [contacts, setContacts] = useState<FarmContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<FarmContact | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    contact_role: "other",
    is_primary: false,
  });

  const fetchContacts = async () => {
    if (!farmId) return;
    setLoading(true);
    try {
      const data = await listFarmContacts(farmId);
      setContacts(data);
    } catch (e) {
      console.error("Failed to load contacts:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [farmId]);

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (!form.first_name.trim()) newErrors.first_name = "Le prénom est requis";
    if (!form.last_name.trim()) newErrors.last_name = "Le nom est requis";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      if (editingContact) {
        await updatePerson(editingContact.person.id, {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
        });
        await updateFarmContact(farmId, editingContact.id, {
          contact_role: form.contact_role,
          is_primary: form.is_primary,
          status: "active",
        });
      } else {
        await addFarmContact(farmId, {
          person: {
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            email: form.email.trim() || undefined,
            phone: form.phone.trim() || undefined,
          },
          contact_role: form.contact_role,
          is_primary: form.is_primary,
        });
      }
      await fetchContacts();
      setForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        contact_role: "other",
        is_primary: false,
      });
      setEditingContact(null);
      setModalOpen(false);
      onContactAdded?.();
    } catch (e) {
      setErrors({
        submit: e instanceof Error ? e.message : "Erreur lors de l'enregistrement du contact",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (c: FarmContact) => {
    setEditingContact(c);
    setForm({
      first_name: c.person.first_name,
      last_name: c.person.last_name,
      email: c.person.email ?? "",
      phone: c.person.phone ?? "",
      contact_role: c.contact_role ?? "other",
      is_primary: c.is_primary,
    });
    setErrors({});
    setModalOpen(true);
  };

  const handleDelete = async (c: FarmContact) => {
    if (!window.confirm(`Supprimer le contact ${c.person.first_name} ${c.person.last_name} ?`)) return;
    setDeletingId(c.id);
    try {
      await deleteFarmContact(farmId, c.id);
      await fetchContacts();
      onContactAdded?.();
    } catch (e) {
      console.error("Failed to delete contact:", e);
      alert(e instanceof Error ? e.message : "Erreur lors de la suppression");
    } finally {
      setDeletingId(null);
    }
  };

  const roleLabel = (role: string) => CONTACT_ROLES.find((r) => r.value === role)?.label ?? role;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text">Contacts</h3>
        <Button
          label="Ajouter un contact"
          intent="primary"
          size="sm"
          onClick={() => {
            setEditingContact(null);
            setForm({
              first_name: "",
              last_name: "",
              email: "",
              phone: "",
              contact_role: "other",
              is_primary: false,
            });
            setErrors({});
            setModalOpen(true);
          }}
        />
      </div>

      {loading ? (
        <div className="text-sm text-muted py-6 text-center">Chargement...</div>
      ) : contacts.length === 0 ? (
        <div className="rounded-md border border-border bg-surface p-6 text-center text-sm text-muted">
          Aucun contact. Cliquez sur « Ajouter un contact » pour en créer un.
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map((c) => (
            <div
              key={c.id}
              className="rounded-md border border-border bg-surface p-4 shadow-sm flex flex-wrap items-start gap-4"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="rounded-full bg-surface-muted p-2 shrink-0">
                  <User size={18} className="text-muted" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-text flex items-center gap-1.5 flex-wrap">
                    <span>
                      {c.person.first_name} {c.person.last_name}
                    </span>
                    {c.is_primary && (
                      <Star size={16} className="shrink-0 fill-amber-400 text-amber-400" aria-label="Principal" />
                    )}
                  </p>
                  <p className="text-xs text-muted">{roleLabel(c.contact_role)}</p>
                </div>
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                {c.person.email && (
                  <a
                    href={`mailto:${c.person.email}`}
                    className="text-sm text-primary hover:underline flex items-center gap-1.5"
                  >
                    <Mail size={14} />
                    {c.person.email}
                  </a>
                )}
                {c.person.phone && (
                  <span className="text-sm text-text flex items-center gap-1.5">
                    <Phone size={14} />
                    {c.person.phone}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => handleEdit(c)}
                  title="Modifier"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-surface text-text hover:bg-surface-muted"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(c)}
                  disabled={deletingId === c.id}
                  title="Supprimer"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-danger bg-danger/10 text-danger hover:bg-danger/20 disabled:opacity-50"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        title={editingContact ? "Modifier le contact" : "Ajouter un contact"}
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingContact(null);
        }}
        showDefaultButtons={false}
        showCloseButton
        size="md"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Prénom"
              placeholder="Prénom"
              value={form.first_name}
              onChange={(e) => {
                setForm({ ...form, first_name: e.target.value });
                if (errors.first_name) setErrors({ ...errors, first_name: "" });
              }}
              state={errors.first_name ? "error" : "default"}
            />
            <Input
              label="Nom"
              placeholder="Nom"
              value={form.last_name}
              onChange={(e) => {
                setForm({ ...form, last_name: e.target.value });
                if (errors.last_name) setErrors({ ...errors, last_name: "" });
              }}
              state={errors.last_name ? "error" : "default"}
            />
          </div>
          <Input
            label="Courriel"
            placeholder="courriel@exemple.com"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Input
            label="Téléphone"
            placeholder="(514) 555-1234"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <Select
            label="Rôle"
            options={CONTACT_ROLES.map((r) => r.label)}
            value={CONTACT_ROLES.find((r) => r.value === form.contact_role)?.label ?? form.contact_role}
            onChange={(label) =>
              setForm({
                ...form,
                contact_role: CONTACT_ROLES.find((r) => r.label === label)?.value ?? "other",
              })
            }
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_primary}
              onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
              className="rounded border-border"
            />
            <span className="text-sm text-text">Contact principal</span>
          </label>
          {errors.submit && (
            <p className="text-sm text-danger">{errors.submit}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              label="Annuler"
              intent="default"
              size="sm"
              onClick={() => {
                setModalOpen(false);
                setEditingContact(null);
              }}
            />
            <Button
              label={editingContact ? "Enregistrer" : "Ajouter"}
              intent="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={saving || !form.first_name.trim() || !form.last_name.trim()}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
